import { useState } from 'react';
import type { ContactMailStatus } from '../api/mailing';

function fmtDay(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ');
}

export function mailStatusTitle(status: ContactMailStatus): string {
  const since = fmtDay(status.since);
  if (status.status === 'bounced') {
    return `Hard bounce${since ? ` (${since})` : ''}: e-mail se vrátil jako nedoručitelný, mailing se na něj neodesílá.${status.reason ? `\n${status.reason}` : ''}\n„Povolit znovu“ adresu vrátí do mailingu; když se e-mail vrátí znovu, označí se znovu.`;
  }
  return `Neaktivní${since ? ` (${since})` : ''}: ${status.reason ?? 'poslední e-maily bez prokliku'}, mailing se neodesílá.\n„Aktivovat“ kontakt vrátí do mailingu.`;
}

const TONE = {
  bounced: { dot: '#d92d20', bg: '#fde2e1', fg: '#b42318', label: 'Hard bounce', action: 'Povolit znovu' },
  inactive: { dot: '#f79009', bg: '#fff4e5', fg: '#b54708', label: 'Neaktivní', action: 'Aktivovat' },
} as const;

export function MailMark({ tone, label, title }: { tone: 'bounced' | 'inactive' | 'unsubscribed'; label: string; title: string }) {
  const t = tone === 'unsubscribed' ? TONE.bounced : TONE[tone];
  return (
    <span
      className={`mail-status-mark is-${tone}`}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 6, padding: '1px 7px 1px 6px', borderRadius: 999,
        fontSize: 10, fontWeight: 650, lineHeight: 1.6, verticalAlign: 'middle', whiteSpace: 'nowrap', background: t.bg, color: t.fg,
      }}
    >
      <i aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, minWidth: 8, borderRadius: '50%', background: t.dot, boxShadow: `0 0 0 2px ${t.dot}33` }} />
      <span>{label}</span>
    </span>
  );
}

export function MailStatusDot({ status, onClear }: { status: ContactMailStatus | null | undefined; onClear?: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!status) return null;
  const tone = TONE[status.status];

  async function clear() {
    if (!onClear || busy) return;
    setBusy(true); setErr(null);
    try { await onClear(); } catch (e) { setErr(e instanceof Error ? e.message : 'Nepodařilo se uložit'); } finally { setBusy(false); }
  }

  return (
    <span
      className={`mail-status-mark is-${status.status}`}
      title={err ?? mailStatusTitle(status)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 6, padding: '1px 7px 1px 6px', borderRadius: 999,
        fontSize: 10, fontWeight: 650, lineHeight: 1.6, verticalAlign: 'middle', whiteSpace: 'nowrap',
        background: tone.bg, color: tone.fg, opacity: busy ? 0.6 : 1,
      }}
    >
      <i aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, minWidth: 8, borderRadius: '50%', background: tone.dot, boxShadow: `0 0 0 2px ${tone.dot}33` }} />
      <span>{err ? 'Chyba' : tone.label}</span>
      {onClear && (
        <button
          type="button"
          disabled={busy}
          onClick={clear}
          style={{ border: 0, padding: 0, margin: 0, background: 'none', color: tone.fg, font: 'inherit', textDecoration: 'underline', cursor: busy ? 'default' : 'pointer', minHeight: 0, lineHeight: 'inherit' }}
        >
          {busy ? 'Ukládám…' : tone.action}
        </button>
      )}
    </span>
  );
}
